import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/Button'
import { Field, Input, Select } from '@/components/ui/Field'
import { Page, PageHeader, Section } from '@/components/ui/Page'
import { ErrorState } from '@/components/ui/States'
import { JOB_KIND_LABEL, type JobKind } from '@/lib/jobs'
import { VenuePicker } from '@/features/venues/VenuePicker'
import { useCurrentStaff } from '@/features/auth/RequireStaff'
import { describeError, supabase } from '@/lib/supabase'
import { useStages } from './queries'

/**
 * Adding a wedding directly, for the ones booked before this system existed and
 * for the ones that never went through an enquiry.
 *
 * The couple's contact records are created here too, which is why this is a
 * hand-rolled sequence rather than a single insert. It is not atomic the way
 * `convert_inquiry_to_job` is — if the job insert fails, two contacts are left
 * behind. That is a deliberate trade: adding a wedding by hand happens a few
 * times a year, the failure is visible immediately, and a stray contact is
 * harmless and editable. The enquiry path, which runs constantly, gets the
 * database function.
 */
export function NewWeddingPage() {
  const navigate = useNavigate()
  const { staff } = useCurrentStaff()
  const stagesQuery = useStages()

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [venueId, setVenueId] = useState<string | null>(null)

  const [form, setForm] = useState({
    kind: 'wedding' as JobKind,
    title: '',
    associate_for: '',
    associate_role: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    partner_first_name: '',
    partner_last_name: '',
    event_date: '',
    guest_count: '',
    services: '',
    booked: 'yes',
  })

  function set<K extends keyof typeof form>(key: K, value: string) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  const isCouple = form.kind === 'wedding' || form.kind === 'couple_shoot'

  // The column follows from booked_at and the date (jobs_auto_stage); the
  // stage given here is only a placeholder the trigger replaces.
  const stageId = stagesQuery.data?.[0]?.id ?? ''

  const ready = form.first_name.trim() !== '' && form.event_date !== '' && stageId !== ''

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (!ready) return

    setSaving(true)
    setError(null)

    try {
      const { data: primary, error: primaryError } = await supabase
        .from('contacts')
        .insert({
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim() || null,
          email: form.email.trim() || null,
          phone: form.phone.trim() || null,
        })
        .select('id')
        .single()

      if (primaryError) throw primaryError

      let partnerId: string | null = null
      if (form.partner_first_name.trim()) {
        const { data: partner, error: partnerError } = await supabase
          .from('contacts')
          .insert({
            first_name: form.partner_first_name.trim(),
            last_name: form.partner_last_name.trim() || null,
          })
          .select('id')
          .single()

        if (partnerError) throw partnerError
        partnerId = partner.id
      }

      // Copy the venue's measurements onto the job, so editing the venue later
      // never silently rewrites a wedding that has already been quoted.
      let venueFields = {}
      if (venueId) {
        const { data: venue } = await supabase.from('venues').select('*').eq('id', venueId).single()
        if (venue) {
          venueFields = {
            venue_id: venue.id,
            venue_name: venue.name,
            venue_address: venue.formatted_address,
            venue_lat: venue.latitude,
            venue_lng: venue.longitude,
            distance_km: venue.distance_km,
            travel_duration_minutes: venue.travel_duration_minutes,
            distance_source: venue.distance_km === null ? null : ('venue' as const),
            distance_calculated_at: venue.distance_km === null ? null : new Date().toISOString(),
          }
        }
      }

      const guests = form.guest_count.trim() === '' ? null : Number(form.guest_count)

      const { data: job, error: jobError } = await supabase
        .from('jobs')
        .insert({
          primary_contact_id: primary.id,
          partner_contact_id: partnerId,
          pipeline_stage_id: stageId,
          // Booked before the Studio: no ticks to make, it is simply booked.
          booked_at: form.booked === 'yes' ? new Date().toISOString() : null,
          event_date: form.event_date,
          kind: form.kind,
          title: form.title.trim() || null,
          associate_for: form.kind === 'associate' ? form.associate_for.trim() || null : null,
          associate_role: form.kind === 'associate' ? form.associate_role.trim() || null : null,
          guest_count: guests !== null && Number.isFinite(guests) ? Math.round(guests) : null,
          services: form.services ? (form.services as 'photography' | 'videography' | 'both') : null,
          created_by: staff.id,
          ...venueFields,
        })
        .select('id')
        .single()

      if (jobError) throw jobError

      toast.success(isCouple ? 'Added' : 'Job added')
      void navigate(`/weddings/${job.id}`)
    } catch (caught) {
      setError(describeError(caught))
      setSaving(false)
    }
  }

  return (
    <Page>
      <form onSubmit={(event) => void submit(event)}>
        <PageHeader
          eyebrow="Projects"
          title="New project"
          actions={
            <>
              <Button variant="ghost" onClick={() => void navigate('/projects')}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={saving} loadingLabel="Adding" disabled={!ready}>
                Add
              </Button>
            </>
          }
        />

        {error ? <ErrorState className="mb-5" title="Could not add the project" detail={error} /> : null}

        <Section title="What kind of job">
          <div className="grid max-w-3xl gap-x-8 gap-y-5 sm:grid-cols-2">
            <Field label="Type">
              <Select value={form.kind} onChange={(event) => set('kind', event.target.value)}>
                {(Object.keys(JOB_KIND_LABEL) as JobKind[]).map((key) => (
                  <option key={key} value={key}>
                    {JOB_KIND_LABEL[key]}
                  </option>
                ))}
              </Select>
            </Field>
            {!isCouple ? (
              <Field label="Name" hint='e.g. "CORE Fruit year-end function"'>
                <Input value={form.title} onChange={(event) => set('title', event.target.value)} />
              </Field>
            ) : null}
            {form.kind === 'associate' ? (
              <>
                <Field label="Shooting for" hint="The photographer or company who booked you.">
                  <Input value={form.associate_for} onChange={(event) => set('associate_for', event.target.value)} />
                </Field>
                <Field label="Your role" hint="Second photographer, lead videographer…">
                  <Input value={form.associate_role} onChange={(event) => set('associate_role', event.target.value)} />
                </Field>
              </>
            ) : null}
          </div>
        </Section>

        <Section title={isCouple ? 'The couple' : 'The client'}>
          <div className="grid max-w-3xl gap-x-8 gap-y-5 sm:grid-cols-2">
            <Field label="First name" required>
              <Input value={form.first_name} onChange={(event) => set('first_name', event.target.value)} required autoFocus />
            </Field>
            <Field label="Last name">
              <Input value={form.last_name} onChange={(event) => set('last_name', event.target.value)} />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.email} onChange={(event) => set('email', event.target.value)} />
            </Field>
            <Field label="Phone">
              <Input type="tel" value={form.phone} onChange={(event) => set('phone', event.target.value)} />
            </Field>
            {isCouple ? (<>
            <Field label="Partner's first name">
              <Input value={form.partner_first_name} onChange={(event) => set('partner_first_name', event.target.value)} />
            </Field>
            <Field label="Partner's last name">
              <Input value={form.partner_last_name} onChange={(event) => set('partner_last_name', event.target.value)} />
            </Field>
            </>) : null}
          </div>
        </Section>

        <Section title={isCouple ? 'The day' : 'The date'}>
          <div className="grid max-w-3xl gap-x-8 gap-y-5 sm:grid-cols-2">
            <Field label="Date" required>
              <Input type="date" value={form.event_date} onChange={(event) => set('event_date', event.target.value)} required />
            </Field>
            <Field label="Guests">
              <Input
                type="number"
                min="0"
                max="5000"
                value={form.guest_count}
                onChange={(event) => set('guest_count', event.target.value)}
              />
            </Field>
            <Field label="What they booked">
              <Select value={form.services} onChange={(event) => set('services', event.target.value)}>
                <option value="">Not decided</option>
                <option value="photography">Photography</option>
                <option value="videography">Film</option>
                <option value="both">Photo + film</option>
              </Select>
            </Field>
            <Field label="Booked?" hint="Not yet: it waits in Enquiries until the contract and deposit are ticked.">
              <Select value={form.booked} onChange={(event) => set('booked', event.target.value)}>
                <option value="yes">Yes, contract and deposit are in</option>
                <option value="no">Not yet</option>
              </Select>
            </Field>
          </div>
        </Section>

        <Section title="Venue" description="Linking one from the library brings its distance with it.">
          <VenuePicker value={venueId} onChange={setVenueId} />
        </Section>
      </form>
    </Page>
  )
}
